import React, { useState } from "react";
import { playResult } from "@/lib/feedbackSounds";
import { Volume2, Snail, Headphones, Check, X } from "lucide-react";
import { TERM_AUDIO } from "@/data/termAudio";

let current = null;
const play = (term, slow = false) => {
  if (current) { current.pause(); current.currentTime = 0; }
  const a = new Audio(TERM_AUDIO[term]);
  a.preservesPitch = true;
  a.playbackRate = slow ? 0.6 : 1;
  current = a;
  return a.play().then(() => a).catch(() => null);
};

const TermCard = ({ sign, term, meaning, testId }) => {
  const [playing, setPlaying] = useState(false);
  const src = TERM_AUDIO[term];
  const say = async (slow) => {
    setPlaying(true);
    const a = await play(term, slow);
    if (a) a.onended = () => setPlaying(false); else setPlaying(false);
  };
  const body = <>
    <span className="flex h-12 min-w-[3rem] items-center justify-center rounded-xl bg-primary/10 px-2 font-serif text-lg italic text-primary">{sign}</span>
    <div className="min-w-0 flex-1"><p className="font-medium italic text-foreground">{term}</p><p className="text-xs text-muted-foreground">{meaning}</p></div>
    {src && <Volume2 size={16} className={`shrink-0 ${playing ? "animate-pulse text-primary" : "text-muted-foreground"}`} />}
  </>;
  const cls = "flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left";
  if (!src) return <div className={cls}>{body}</div>;
  return (
    <div className="relative h-full">
      <button type="button" onClick={() => say(false)} aria-label={`Hear how to say ${term}`} data-testid={testId}
        className={`${cls} h-full w-full pb-7 transition-colors hover:border-primary/60 hover:bg-primary/5`}>{body}</button>
      <button type="button" onClick={() => say(true)} aria-label={`Hear ${term} slowly`} data-testid={`${testId}-slow`}
        className="absolute bottom-1.5 right-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold text-primary hover:bg-primary/10"><Snail size={12} /> Say it slowly</button>
    </div>
  );
};

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

const ListeningQuiz = ({ list, i }) => {
  const [q, setQ] = useState(null);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState({ right: 0, asked: 0 });
  const next = () => {
    const [, term, meaning] = list[Math.floor(Math.random() * list.length)];
    const others = shuffle([...new Set(list.filter((t) => t[2] !== meaning).map((t) => t[2]))]).slice(0, 2);
    setQ({ term, meaning, options: shuffle([meaning, ...others]) });
    setPicked(null);
    play(term);
  };
  const choose = (o) => {
    if (picked) return;
    setPicked(o);
    playResult(o === q.meaning);
    setScore((s) => ({ right: s.right + (o === q.meaning ? 1 : 0), asked: s.asked + 1 }));
  };
  if (!q) return (
    <button type="button" onClick={next} data-testid={`terms-quiz-start-${i}`}
      className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"><Headphones size={15} /> Listening quiz: hear a word, pick its meaning</button>
  );
  return (
    <div className="mt-4 rounded-2xl border border-primary/40 bg-primary/5 p-4" data-testid={`terms-quiz-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-bold"><Headphones size={15} className="text-primary" /> What does this word mean?</p>
        <p className="font-mono text-xs text-muted-foreground" data-testid={`terms-quiz-score-${i}`}>{score.right} / {score.asked}</p>
      </div>
      <button type="button" onClick={() => play(q.term)} data-testid={`terms-quiz-replay-${i}`} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground"><Volume2 size={13} /> Hear it again</button>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {q.options.map((o, k) => {
          const state = picked && (o === q.meaning ? "right" : o === picked ? "wrong" : "");
          return (
            <button key={o} type="button" onClick={() => choose(o)} data-testid={`terms-quiz-option-${i}-${k}`}
              className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors ${state === "right" ? "border-emerald-500 bg-emerald-500/15" : state === "wrong" ? "border-red-500 bg-red-500/15" : "border-border bg-card hover:border-primary/60"}`}>
              {o}{state === "right" && <Check size={15} className="text-emerald-500" />}{state === "wrong" && <X size={15} className="text-red-500" />}
            </button>
          );
        })}
      </div>
      {picked && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="text-sm" data-testid={`terms-quiz-feedback-${i}`}>{picked === q.meaning ? "Correct!" : "Not quite."} <span className="font-semibold italic">{q.term}</span> means "{q.meaning}".</p>
          <button type="button" onClick={next} data-testid={`terms-quiz-next-${i}`} className="rounded-full border border-border px-3 py-1 text-xs font-semibold hover:border-primary">Next word</button>
        </div>
      )}
    </div>
  );
};

export const TermsGrid = ({ s, i }) => {
  const spoken = s.terms.filter(([, term]) => TERM_AUDIO[term]);
  return (
    <div className="mt-5" data-testid={`lesson-terms-${i}`}>
      {s.about && <p className="mb-3 text-base font-semibold text-foreground" data-testid={`lesson-terms-about-${i}`}>{s.about}</p>}
      {spoken.length > 0 && <p className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary" data-testid={`lesson-terms-pronounce-${i}`}>
        <Volume2 size={13} /> Tap to hear pronunciation: press any word to hear how to say it</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {s.terms.map(([sign, term, meaning], k) => <TermCard key={term} sign={sign} term={term} meaning={meaning} testId={`lesson-term-${i}-${k}`} />)}
      </div>
      {spoken.length >= 3 && <ListeningQuiz list={spoken} i={i} />}
    </div>
  );
};
